import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { useQueryRequest } from '../core/QueryRequestProvider'
import { useListFilters } from '../core/ListFiltersProvider'
import { CustomTable, TableEmptyState } from '../../../../../components'
import { notaColumns } from './columns/_columns'
import { NotaExpandedRow } from './columns/NotaExpandedRow'
import { NotaList, NotaView } from '../core/_models'
import { getNotaById, getNotaByProfissionalById } from '../core/_requests'
import { Permission } from '../../../../../../_metronic/helpers'
import { useAuth } from '../../../../auth'
import { useT } from '_metronic/i18n/Metronici18n'

export const NotaTable = () => {
  const { isLoading, isError, refetch, response } = useQueryResponse()
  const { filters, hasFilters, clearFilters } = useListFilters()
  const { currentUser } = useAuth()
  const { t } = useT()
  const location = useLocation()
  const isProfissionalRoute = location.pathname.includes('/profissional')

  const columns = useMemo(() => notaColumns(t, isProfissionalRoute), [isProfissionalRoute, t])

  // Admin lista por instituição: sem ela, a tabela pede a seleção em vez de "nenhuma nota".
  const aguardandoEmpresa = currentUser?.grupo?.nome === Permission.Admin && !filters.empresa

  const emptyState = aguardandoEmpresa ? (
    <TableEmptyState
      title={`Selecione uma ${t('empresa.label.lowercase')}`}
      description={`As notas são listadas por ${t(
        'empresa.label.lowercase'
      )}. Escolha uma no filtro acima.`}
    />
  ) : hasFilters ? (
    <TableEmptyState
      title='Nenhuma nota encontrada'
      description='Revise a instituição, o período e os demais filtros.'
      action={{ label: 'Limpar filtros', onClick: clearFilters }}
    />
  ) : (
    <TableEmptyState
      title='Nenhuma nota ainda'
      description={
        isProfissionalRoute
          ? 'Suas notas aparecem aqui quando um repasse for gerado.'
          : 'As notas dos profissionais aparecem aqui quando os repasses forem gerados.'
      }
    />
  )

  return (
    <CustomTable<NotaList>
      id='kt_table_notas'
      ariaLabel={t('nota.plural')}
      itemLabel={{ singular: 'nota', plural: 'notas' }}
      data={response?.data || []}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      emptyState={emptyState}
      queryHooks={{ useQueryRequest, useQueryResponse }}
      // Detalhe completo (datas, observação, validação) vem do endpoint da nota.
      enableExpand
      onExpandFetch={(row) =>
        isProfissionalRoute
          ? getNotaByProfissionalById(Number(row.original.id))
          : getNotaById(Number(row.original.id))
      }
      getExpandLabel={(row) => `nota ${row.profissional?.nome ?? row.id}`}
      renderExpand={(data: NotaView, loading) => (
        <NotaExpandedRow data={data} isLoading={loading} isProfissional={isProfissionalRoute} />
      )}
    />
  )
}
